import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loader from "react-loader-spinner";
import { httpGet } from "../../components/helpers/httpMethods";
import { UserDataContext } from "../../context/userContext";
import { CartContextData } from "../../context/newsPaperContext";
import "./publicationPage.css";
import SEO from "../Layout/SEO";

/*
 * Public edition-day page (/publication/:id/:date): cover and top headlines only (never the PDF or page text).
 * Search engines get the same content from the server first (api/seo/render). Buttons reuse the paper page's
 * flows: the reader (which sends old issues to the archive checkout), the existing checkout modal, and login.
 */

const SITE = "https://newstand.tech";
const niceName = (n) => String(n || "").trim().toLowerCase().replace(/(^|[\s\-/(])([a-z])/g, (m, a, b) => a + b.toUpperCase());
const fmt = (day, opts) => {
  try {
    return new Date(`${day}T12:00:00Z`).toLocaleDateString("en-GB", { timeZone: "UTC", ...opts });
  } catch (e) {
    return day;
  }
};
const longDate = (d) => fmt(d, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const shortDate = (d) => fmt(d, { day: "numeric", month: "long", year: "numeric" });
const naira = (v) => `₦${Number(v).toLocaleString("en-NG")}`;

export default function EditionPage(props) {
  const { userIsLogin, checkUserIsLogin } = useContext(UserDataContext);
  const { onOpenModal, mySubs } = useContext(CartContextData);
  const { id: paperParam, date } = props.match.params;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    httpGet(`seo/edition/${encodeURIComponent(paperParam)}/${encodeURIComponent(date)}`).then((res) => {
      if (!live) return;
      if (res && res.data && res.data.edition) setData(res.data);
      else if (res && res.er && res.er.data && res.er.data.paper) setData(res.er.data);
      else setData(null);
      setLoading(false);
    });
    checkUserIsLogin();
    return () => {
      live = false;
    };
  }, [paperParam, date]);

  if (loading) {
    return (
      <div className="row h-100 justify-content-center align-items-center app-spinner">
        <Loader type="TailSpin" color="#EF6C00" height={50} width={50} />
      </div>
    );
  }

  if (!data || !data.paper) {
    return (
      <div className="pp-page">
        <SEO title="Page not found | TheNewspaperStand" noindex />
        <div className="pp-empty">
          <h1 className="pp-empty-title">We couldn’t find this publication</h1>
          <p>It may have been renamed or removed.</p>
          <button type="button" className="pp-btn pp-btn-primary" onClick={() => props.history.push("/")}>
            Back to all papers
          </button>
        </div>
      </div>
    );
  }

  const { paper, edition, archive, prev, next } = data;
  const highlights = data.highlights || [];
  const nice = niceName(paper.name);
  const paperPath = `/publication/${paper.slug}`;

  const dayLinks = (
    <nav className="pp-crumbs" aria-label="Other days">
      {prev ? <Link className="pp-link-quiet" to={`${paperPath}/${prev}`}>← {shortDate(prev)}</Link> : null}
      {prev && next ? <span aria-hidden="true">·</span> : null}
      {next ? <Link className="pp-link-quiet" to={`${paperPath}/${next}`}>{shortDate(next)} →</Link> : null}
    </nav>
  );

  if (!edition) {
    return (
      <div className="pp-page">
        <SEO title={`${nice}: no edition on this day | TheNewspaperStand`} noindex />
        <nav className="pp-crumbs" aria-label="Breadcrumb">
          <Link className="pp-link-quiet" to="/">All papers</Link>
          <span aria-hidden="true">/</span>
          <Link className="pp-link-quiet" to={paperPath}>{paper.name}</Link>
        </nav>
        <div className="pp-empty">
          <h1 className="pp-empty-title">No {nice} edition on this day</h1>
          <p>Try a day nearby, or see the latest edition.</p>
          {dayLinks}
          <button type="button" className="pp-btn pp-btn-primary" onClick={() => props.history.push(paperPath)}>
            See the latest {nice}
          </button>
        </div>
      </div>
    );
  }

  const day = edition.day;
  const when = longDate(day);
  const subscribed = Array.isArray(mySubs) && mySubs.includes(paper.id);
  const canRead = !archive && (subscribed || paper.free);

  const goToLogin = () => {
    localStorage.setItem("comingFromPublicPublication", "true");
    localStorage.setItem("rediretURL", `${paper.slug}/${day}`);
    props.history.push("/login");
  };

  // Same flow as the paper page; old issues go through the reader, which opens them or sends to the archive checkout.
  const openEdition = () => {
    if (archive) {
      if (userIsLogin === true) props.history.push(`/view_single_paper/${edition.id}`);
      else props.history.push(archive.available ? `/archive?buy=${edition.id}` : "/archive");
      return;
    }
    if (userIsLogin !== true) return goToLogin();
    if (canRead) props.history.push(`/view_single_paper/${edition.id}`);
    else onOpenModal({ id: edition.id, publicationId: paper.id }, "fromPublicPublication");
  };

  let primary = "Read this edition";
  if (archive) {
    if (userIsLogin === true) primary = "Open this old issue";
    else primary = archive.available ? `Buy this old issue${archive.priceNgn ? ` · ${naira(archive.priceNgn)}` : ""}` : "Ask for this old issue";
  } else if (!canRead && paper.oneOffFee) {
    primary = `Buy this edition · ${naira(paper.oneOffFee)}`;
  } else if (!canRead) {
    primary = "Get this edition";
  }

  const lead = highlights.slice(0, 3).map((h) => h.headline).join("; ");
  let description = `${nice} for ${when}: front page and top headlines${lead ? `: ${lead}` : ""}. Read the full e-paper on TheNewspaperStand.`;
  if (description.length > 300) description = `${description.slice(0, 297).replace(/\s+\S*$/, "")}…`;

  return (
    <div className="pp-page">
      <SEO
        title={`${nice}, ${shortDate(day)}: Front Page and Headlines | TheNewspaperStand`}
        description={description}
        url={`${SITE}${paperPath}/${day}`}
        image={/^https?:/.test(edition.image || "") ? edition.image : undefined}
      />
      <nav className="pp-crumbs" aria-label="Breadcrumb">
        <Link className="pp-link-quiet" to="/">All papers</Link>
        <span aria-hidden="true">/</span>
        <Link className="pp-link-quiet" to={paperPath}>{paper.name}</Link>
        <span aria-hidden="true">/</span>
        <span>{shortDate(day)}</span>
      </nav>

      <div className="pp-main">
        <section className="pp-card pp-hero">
          <button type="button" className="pp-hero-cover" onClick={openEdition} aria-label={`Open the ${when} edition`}>
            <img src={edition.image} alt={`${paper.name} front page, ${when}`} />
          </button>
          <div className="pp-hero-body">
            <div className="pp-eyebrow-row">
              <span className="pp-eyebrow">{archive ? "Old issue" : nice}</span>
              {paper.free && !archive ? <span className="pp-chip pp-chip-green">Free to read</span> : null}
              {subscribed && !archive ? <span className="pp-chip pp-chip-blue">You’re subscribed</span> : null}
            </div>
            <h1 className="pp-hero-date">{nice}, {when}</h1>
            {highlights.length > 0 ? (
              <div className="pp-inside">
                <span className="pp-inside-label">Top headlines in this edition</span>
                <ul className="pp-inside-list">
                  {highlights.map((h) => (
                    <li key={`${h.page}-${h.headline}`}>
                      <button
                        type="button"
                        className="pp-inside-item"
                        onClick={() =>
                          canRead && userIsLogin === true
                            ? props.history.push(`/view_single_paper/${edition.id}?page=${h.page}`)
                            : openEdition()
                        }
                      >
                        <span className="pp-inside-headline">{h.headline}</span>
                        <span className="pp-inside-page">p. {h.page}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="pp-hero-actions">
              <button type="button" className="pp-btn pp-btn-primary pp-btn-lg" onClick={openEdition}>
                {primary}
              </button>
              {!archive && !canRead && paper.hasPlans ? (
                <button type="button" className="pp-btn pp-btn-outline pp-btn-lg" onClick={openEdition}>
                  Subscribe
                </button>
              ) : null}
              <button type="button" className="pp-btn pp-btn-outline pp-btn-lg" onClick={() => props.history.push(paperPath)}>
                More from {nice}
              </button>
            </div>
            {dayLinks}
          </div>
        </section>
      </div>
    </div>
  );
}
